import { API_BASE_URL } from "../../config/apiConfig";
import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import ProfileHeader from "../../components/profile/ProfileHeader";
import ProfileCompletionBar from "../../components/profile/ProfileCompletionBar";
import ProfileSectionCard from "../../components/profile/ProfileSectionCard";
import ProfileSummaryCard from "../../components/profile/ProfileSummaryCard";
import PageLoader from "../../components/common/PageLoader";

import calculateProfessionalStatus from "../../utils/calculateProfessionalStatus";

import "../../styles/pages/professional/ProfessionalProfile.css";

const API_BASE =
  API_BASE_URL;

/* ========================================
   AUTH HELPERS
======================================== */

const getToken = () =>
  localStorage.getItem(
    "zethAuthToken"
  ) ||
  sessionStorage.getItem(
    "zethAuthToken"
  );

const clearAuthentication = () => {
  localStorage.removeItem(
    "zethAuthToken"
  );

  localStorage.removeItem(
    "zethUser"
  );

  sessionStorage.removeItem(
    "zethAuthToken"
  );

  sessionStorage.removeItem(
    "zethUser"
  );
};

const parseJsonResponse =
  async (response) => {
    const contentType =
      response.headers.get(
        "content-type"
      );

    if (
      !contentType?.includes(
        "application/json"
      )
    ) {
      return {};
    }

    return response
      .json()
      .catch(() => ({}));
  };

const normalizeArray = (
  value
) =>
  Array.isArray(value)
    ? value
    : [];

/* ========================================
   PROFESSIONAL PROFILE
======================================== */

const ProfessionalProfile = () => {
  const navigate =
    useNavigate();

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    showLoadingIndicator,
    setShowLoadingIndicator,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    profile,
    setProfile,
  ] = useState(null);

  const [
    licences,
    setLicences,
  ] = useState([]);

  const [
    specialties,
    setSpecialties,
  ] = useState([]);

  const [
    documents,
    setDocuments,
  ] = useState([]);

  const [
    certifications,
    setCertifications,
  ] = useState([]);

  const [
    availability,
    setAvailability,
  ] = useState(null);

  /* ========================================
     DELAYED PAGE LOADER
  ======================================== */

  useEffect(() => {
    if (!loading) {
      setShowLoadingIndicator(
        false
      );

      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          setShowLoadingIndicator(
            true
          );
        },
        800
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    loading,
  ]);

  /* ========================================
     LOAD PROFESSIONAL PROFILE DATA
  ======================================== */

  useEffect(() => {
    const loadProfessionalProfile =
      async () => {
        const token =
          getToken();

        if (!token) {
          navigate(
            "/professional/login",
            {
              replace: true,
            }
          );

          return;
        }

        try {
          setLoading(true);
          setError("");

          const requestOptions = {
            method: "GET",

            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          };

          const [
            profileResponse,
            licenceResponse,
            specialtyResponse,
            documentsResponse,
            certificationsResponse,
            availabilityResponse,
          ] = await Promise.all([
            fetch(
              `${API_BASE}/profile`,
              requestOptions
            ),

            fetch(
              `${API_BASE}/professionals/licenses`,
              requestOptions
            ),

            fetch(
              `${API_BASE}/professionals/specialties`,
              requestOptions
            ),

            fetch(
              `${API_BASE}/professional/documents`,
              requestOptions
            ),

            fetch(
              `${API_BASE}/professionals/certifications`,
              requestOptions
            ),

            fetch(
              `${API_BASE}/professionals/availability`,
              requestOptions
            ),
          ]);

          /* ========================================
             AUTHENTICATION CHECK
          ======================================== */

          const protectedResponses = [
            profileResponse,
            licenceResponse,
            specialtyResponse,
            documentsResponse,
            certificationsResponse,
            availabilityResponse,
          ];

          const authenticationFailed =
            protectedResponses.some(
              (response) =>
                response.status ===
                  401 ||
                response.status ===
                  403
            );

          if (
            authenticationFailed
          ) {
            clearAuthentication();

            navigate(
              "/professional/login",
              {
                replace: true,
              }
            );

            return;
          }

          /* ========================================
             PARSE RESPONSES
          ======================================== */

          const [
            profileData,
            licenceData,
            specialtyData,
            documentsData,
            certificationsData,
            availabilityData,
          ] = await Promise.all([
            parseJsonResponse(
              profileResponse
            ),

            parseJsonResponse(
              licenceResponse
            ),

            parseJsonResponse(
              specialtyResponse
            ),

            parseJsonResponse(
              documentsResponse
            ),

            parseJsonResponse(
              certificationsResponse
            ),

            parseJsonResponse(
              availabilityResponse
            ),
          ]);

          /* ========================================
             RESPONSE VALIDATION
          ======================================== */

          if (
            !profileResponse.ok
          ) {
            throw new Error(
              profileData.message ||
                "Unable to load personal information."
            );
          }

          if (
            !licenceResponse.ok
          ) {
            throw new Error(
              licenceData.message ||
                "Unable to load professional licences."
            );
          }

          if (
            !specialtyResponse.ok
          ) {
            throw new Error(
              specialtyData.message ||
                "Unable to load clinical specialties."
            );
          }

          if (
            !documentsResponse.ok
          ) {
            throw new Error(
              documentsData.message ||
                "Unable to load professional documents."
            );
          }

          if (
            !certificationsResponse.ok &&
            certificationsResponse.status !==
              404
          ) {
            throw new Error(
              certificationsData.message ||
                "Unable to load professional certifications."
            );
          }

          if (
            !availabilityResponse.ok &&
            availabilityResponse.status !==
              404
          ) {
            throw new Error(
              availabilityData.message ||
                "Unable to load availability."
            );
          }

          /* ========================================
             SAVE RESPONSE DATA
          ======================================== */

          const loadedProfile =
            profileData.user ||
            profileData.profile ||
            profileData.data ||
            null;

          setProfile(
            loadedProfile
          );

          setLicences(
            normalizeArray(
              licenceData.licenses ||
                licenceData.licences ||
                licenceData.data
            )
          );

          setSpecialties(
            normalizeArray(
              specialtyData.specialties ||
                specialtyData.data
            )
          );

          setDocuments(
            normalizeArray(
              documentsData.documents ||
                documentsData.data
            )
          );

          setCertifications(
            certificationsResponse.ok
              ? normalizeArray(
                  certificationsData.certifications ||
                    certificationsData.data
                )
              : []
          );

          setAvailability(
            availabilityResponse.ok
              ? (
                  availabilityData.availability ||
                  availabilityData.data ||
                  null
                )
              : null
          );

          /*
          Keep ProfessionalLayout user
          information synchronized.
          */

          if (
            loadedProfile
          ) {
            const currentStoredUser =
              JSON.parse(
                localStorage.getItem(
                  "zethUser"
                ) || "{}"
              );

            localStorage.setItem(
              "zethUser",
              JSON.stringify({
                ...currentStoredUser,
                ...loadedProfile,
                role:
                  currentStoredUser.role ||
                  "professional",
              })
            );
          }
        } catch (
          loadError
        ) {
          console.error(
            "Professional profile error:",
            loadError
          );

          setError(
            loadError.message ||
              "Unable to load your professional profile."
          );
        } finally {
          setLoading(false);
        }
      };

    loadProfessionalProfile();
  }, [
    navigate,
  ]);

  /* ========================================
     PERSONAL INFORMATION STATUS
  ======================================== */

  const personalInformationComplete =
    useMemo(() => {
      if (!profile) {
        return false;
      }

      const firstName =
        profile.firstName ||
        profile.first_name;

      const lastName =
        profile.lastName ||
        profile.last_name;

      const email =
        profile.email;

      const phone =
        profile.phone ||
        profile.phone_number;

      const profession =
        profile.profession ||
        profile.profession_name;

      const province =
        profile.province ||
        profile.state;

      return Boolean(
        firstName &&
          lastName &&
          email &&
          phone &&
          profession &&
          province
      );
    }, [
      profile,
    ]);

  /* ========================================
     AVAILABILITY STATUS
  ======================================== */

  const availabilityComplete =
    Boolean(
      availability?.is_configured
    );

  const availabilitySummary =
    useMemo(() => {
      if (
        !availabilityComplete
      ) {
        return (
          "Your work availability is not configured."
        );
      }

      const earliestStartDate =
        availability
          .earliest_start_date;

      if (
        !earliestStartDate
      ) {
        return (
          "Your availability preferences are configured."
        );
      }

      const formattedDate =
        new Date(
          `${String(
            earliestStartDate
          ).slice(
            0,
            10
          )}T00:00:00`
        ).toLocaleDateString(
          "en-CA",
          {
            year:
              "numeric",
            month:
              "short",
            day:
              "numeric",
          }
        );

      return (
        `Available from ${formattedDate}.`
      );
    }, [
      availability,
      availabilityComplete,
    ]);

  /* ========================================
     PROFESSIONAL STATUS
  ======================================== */

  const professionalStatus =
    useMemo(() => {
      return calculateProfessionalStatus({
        personalInformationComplete,
        licences,
        specialties,
        certifications,
        documents,
        availability,
      });
    }, [
      personalInformationComplete,
      licences,
      specialties,
      certifications,
      documents,
      availability,
    ]);

  /* ========================================
     SPECIALTY SUMMARY
  ======================================== */

  const specialtySummary =
    useMemo(() => {
      if (
        specialties.length ===
        0
      ) {
        return (
          "No specialties selected"
        );
      }

      return specialties
        .map(
          (item) =>
            item.specialty ||
            item.specialty_name ||
            item.name
        )
        .filter(Boolean)
        .join(", ");
    }, [
      specialties,
    ]);

  /* ========================================
     CREDENTIAL CARD STATUS
  ======================================== */

  const credentialCardStatus =
    useMemo(() => {
      if (
        professionalStatus
          .verification
          .label ===
        "Verified"
      ) {
        return "Complete";
      }

      if (
        licences.length >
          0 ||
        certifications.length >
          0 ||
        documents.length >
          0
      ) {
        return "In Progress";
      }

      return "Not Started";
    }, [
      professionalStatus,
      licences,
      certifications,
      documents,
    ]);

  const credentialCardSummary =
    useMemo(() => {
      const verification =
        professionalStatus
          .verification;

      if (
        verification.total ===
        0
      ) {
        return (
          "Add your licences and required professional documents."
        );
      }

      if (
        verification.label ===
        "Verified"
      ) {
        return (
          "All required credentials have been verified."
        );
      }

      if (
        verification.rejected >
        0
      ) {
        return `${
          verification.rejected
        } credential${
          verification.rejected !==
          1
            ? "s require"
            : " requires"
        } attention.`;
      }

      if (
        verification.expired >
        0
      ) {
        return `${
          verification.expired
        } credential${
          verification.expired !==
          1
            ? "s have"
            : " has"
        } expired.`;
      }

      if (
        verification.missing >
        0
      ) {
        return `${
          verification.missing
        } required credential${
          verification.missing !==
          1
            ? "s are"
            : " is"
        } missing.`;
      }

      return `${verification.verified} of ${verification.total} required credentials verified.`;
    }, [
      professionalStatus,
    ]);

  /* ========================================
     LOADING
  ======================================== */

  if (
    loading &&
    showLoadingIndicator
  ) {
    return (
      <PageLoader
        title="Professional Profile"
        message="Loading your professional profile..."
      />
    );
  }

  if (loading) {
    return null;
  }

  /* ========================================
     PAGE
  ======================================== */

  return (
    <main className="professional-profile-page">

      {/* ========================================
          PAGE HEADER
      ======================================== */}

      <div className="profile-page-header-row">
        <ProfileHeader />

        <button
          type="button"
          className="profile-edit-button"
          onClick={() =>
            navigate(
              "/professional/profile/edit"
            )
          }
        >
          Edit Profile
        </button>
      </div>

      {/* ========================================
          ERROR
      ======================================== */}

      {error && (
        <div
          className="profile-error-message"
          role="alert"
        >
          <div>
            <strong>
              Unable to load profile
            </strong>

            <p>
              {error}
            </p>
          </div>

          <button
            type="button"
            className="secondary-button"
            onClick={() =>
              window.location.reload()
            }
          >
            Try Again
          </button>
        </div>
      )}

      {/* ========================================
          PROFILE STATUS
      ======================================== */}

      <ProfileCompletionBar
        status={
          professionalStatus
        }
      />

      {/* ========================================
          SUMMARY
      ======================================== */}

      <ProfileSummaryCard
        profile={
          profile
        }
        licences={
          licences
        }
        specialties={
          specialties
        }
      />

      {/* ========================================
          PROFILE MANAGEMENT
      ======================================== */}

      <section className="profile-sections-container">
        <div className="profile-sections-heading">
          <div>
            <p className="account-label">
              Profile Management
            </p>

            <h2>
              Professional Information
            </h2>

            <p>
              Keep your information
              current to improve your
              visibility to healthcare
              facilities.
            </p>
          </div>
        </div>

        <div className="profile-sections-grid">

          {/* PERSONAL INFORMATION */}

          <ProfileSectionCard
            title="Personal Information"
            status={
              personalInformationComplete
                ? "Complete"
                : "Incomplete"
            }
            summary={
              personalInformationComplete
                ? "Your personal and contact information is complete."
                : "Add the missing personal and contact information."
            }
            actionLink="/professional/profile/edit"
            icon="user"
          />

          {/* CLINICAL SPECIALTIES */}

          <ProfileSectionCard
            title="Clinical Specialties"
            status={
              specialties.length >
              0
                ? "Complete"
                : "Incomplete"
            }
            summary={
              specialtySummary
            }
            actionLink="/professional/specialties"
            icon="specialty"
          />

          {/* CREDENTIALS */}

          <ProfileSectionCard
            title="Credentials"
            status={
              credentialCardStatus
            }
            summary={
              credentialCardSummary
            }
            actionLink="/professional/credentials"
            icon="credentials"
          />

          {/* AVAILABILITY */}

          <ProfileSectionCard
            title="Availability"
            status={
              availabilityComplete
                ? "Complete"
                : "Not Started"
            }
            summary={
              availabilitySummary
            }
            actionLink="/professional/availability"
            icon="calendar"
          />
        </div>
      </section>
    </main>
  );
};

export default ProfessionalProfile;
